import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /** Whether a cart request is on its way. One at a time: the server may answer two in either order. */
  #requestInFlight = false;

  /** @type {{line: number, quantity: number, action: string} | null} A removal waiting for that answer. */
  #queuedRemoval = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityInput);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityInput);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Answers a quantity change at once: the server takes a second or more, so the prices are worked out here and
   * the controls stay usable.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityInput = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target) || !event.detail.cartLine) return;

    this.classList.add('cart-items-pending');
    this.#showExpectedPrices();
  };

  /**
   * Shows what the cart costs at the quantities now in its inputs. A price the server has to work out (a
   * discounted line, a cart with a discount) shimmers until the answer comes.
   */
  #showExpectedPrices() {
    const { cartTotal } = this.refs;
    const moneyFormat = cartTotal?.dataset.moneyFormat;
    let total = moneyFormat ? Number(cartTotal?.dataset.totalCents) : NaN;

    for (const row of this.#rows) {
      const input = row.querySelector('input[data-cart-quantity]');
      if (!(input instanceof HTMLInputElement)) continue;

      const quantity = parseInt(input.value);
      const quantityInCart = Number(input.dataset.cartQuantity);
      const linePrice = /** @type {TextComponent | null} */ (row.querySelector('text-component'));
      const unitCents = Number(linePrice?.dataset.unitCents);

      if (!moneyFormat || !Number.isFinite(unitCents) || !Number.isFinite(quantity)) {
        if (quantity !== quantityInCart) {
          linePrice?.shimmer();
          total = NaN;
        }
        continue;
      }

      for (const price of row.querySelectorAll('[data-unit-cents]')) {
        if (!(price instanceof HTMLElement)) continue;
        const text = formatCents(Number(price.dataset.unitCents) * quantity, moneyFormat);
        price.textContent = text;
        if (price.hasAttribute('value')) price.setAttribute('value', text);
      }
      total += unitCents * (quantity - quantityInCart);
    }

    if (!cartTotal) return;
    if (!moneyFormat || !Number.isFinite(total)) {
      cartTotal.shimmer();
      return;
    }

    const totalText = formatCents(total, cartTotal.dataset.totalFormat || moneyFormat);
    cartTotal.textContent = totalText;
    cartTotal.setAttribute('value', totalText);

    // The free-shipping line follows: its bar, its amount, and which of its sentences shows.
    for (const line of document.querySelectorAll('.cart-free-shipping[data-threshold-cents]')) {
      if (!(line instanceof HTMLElement)) continue;
      const track = line.querySelector('.cart-free-shipping__track');
      const threshold = Number(line.dataset.thresholdCents);
      if (!(threshold > 0)) continue;

      const progress = Math.min(1, total / threshold);
      if (track instanceof HTMLElement) track.style.setProperty('--progress', String(progress));
      if (threshold > total) {
        for (const remaining of line.querySelectorAll('[data-free-shipping-remaining]')) {
          remaining.textContent = formatCents(threshold - total, moneyFormat);
        }
      }
      if (line.dataset.reached) line.dataset.reached = String(total >= threshold);
      if (line.dataset.nearFrom) line.dataset.near = String(progress >= Number(line.dataset.nearFrom));
    }
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.#sendNextChange();
  }

  /**
   * The rows of the cart as they are on the page now.
   * @returns {HTMLTableRowElement[]} The rows.
   */
  get #rows() {
    return /** @type {HTMLTableRowElement[]} */ ([...this.querySelectorAll('[ref="cartItemRows[]"]')]);
  }

  /**
   * Sends one quantity the page has and the server does not. Nothing goes while a request is on its way: its
   * answer calls this again, so every line gets its turn and the last quantity chosen is the one that stays.
   * @returns {boolean} Whether a request is on its way now.
   */
  #sendNextChange() {
    if (this.#requestInFlight) return true;

    for (const row of this.#rows) {
      const input = row.querySelector('input[data-cart-quantity]');
      if (!(input instanceof HTMLInputElement) || row.classList.contains('removing')) continue;

      const quantity = parseInt(input.value);
      if (!(quantity > 0) || quantity === Number(input.dataset.cartQuantity)) continue;

      this.updateQuantity({ line: Number(input.dataset.cartLine), quantity, action: 'change' });
      return true;
    }

    return false;
  }

  /**
   * Notes on each row the quantity the server now has for it, so the next look for an unsent change compares with
   * the server and not with the page as it was drawn.
   * @param {{line: number, quantity: number, action: string}} sent - The request just answered.
   * @param {{items?: Array<{key: string, quantity: number}>, total_price?: number}} cart - The cart, as the server returned it.
   */
  #noteServerQuantities(sent, { items = [], total_price: totalCents }) {
    // The total the expected prices start from goes with those quantities: left as the page was drawn, it would
    // be counted against the new quantities and could show less than nothing.
    const { cartTotal } = this.refs;
    if (cartTotal?.dataset.totalCents && Number.isFinite(totalCents)) cartTotal.dataset.totalCents = String(totalCents);

    for (const row of this.#rows) {
      const input = row.querySelector('input[data-cart-quantity]');
      if (!(input instanceof HTMLInputElement)) continue;
      // A cart with a discount code gives a line a new key whenever its quantity changes. A quantity change does not
      // move any line, so its place still finds it.
      const item =
        items.find(({ key }) => key === row.dataset.key) ??
        (sent.action === 'change' ? items[Number(input.dataset.cartLine) - 1] : undefined);
      if (!item) continue;

      input.dataset.cartQuantity = String(item.quantity);
      // The server kept another quantity than the one asked for: the page takes the server's, and does not ask again.
      if (Number(input.dataset.cartLine) === sent.line && parseInt(input.value) === sent.quantity) {
        input.value = String(item.quantity);
      }
    }
  }

  /**
   * Ends a run of requests: the cart is usable again and the express buttons may check out.
   */
  #finishRequests() {
    this.#enableCartItems();
    this.classList.remove('cart-items-pending');
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      // The server's answer can come before the animation ends: the redraw then reuses this row for the line
      // after it, which has to stay.
      const { key } = row.dataset;
      const remove = () => row.dataset.key === key && row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    // Only a removal locks the cart: the lines after it are renumbered, so nothing else may be chosen until the
    // server has answered.
    if (config.action !== 'change') {
      this.#disableCartItems();
      this.refs.cartTotal?.shimmer();
    }

    // One request at a time. A quantity change that has to wait is found again when the answer comes
    // (#sendNextChange); a removal is kept for then.
    if (this.#requestInFlight) {
      if (config.action !== 'change') this.#queuedRemoval = config;
      return;
    }
    this.#requestInFlight = true;

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    const { line, quantity } = config;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);
        this.#requestInFlight = false;

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          this.#showExpectedPrices();
        } else {
          this.#noteServerQuantities(config, parsedResponseText);
        }

        // A quantity chosen in the meantime has not reached the server: it goes next, then a removal that was
        // waiting, and the cart is redrawn from the last answer. Redrawing from this one would put an old
        // quantity back.
        if (this.#sendNextChange()) return;
        if (this.#queuedRemoval) {
          const removal = this.#queuedRemoval;
          this.#queuedRemoval = null;
          this.updateQuantity(removal);
          return;
        }

        this.#finishRequests();
        resetShimmer(this);
        if (parsedResponseText.errors) return;

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
        this.#requestInFlight = false;
        this.#queuedRemoval = null;
        this.#finishRequests();
      })
      .finally(() => {
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

/**
 * Writes an amount in cents the way the shop's money format does.
 * @param {number} cents - The amount in cents.
 * @param {string} moneyFormat - The shop's money format, such as "${{amount}}".
 * @returns {string} The formatted amount.
 */
function formatCents(cents, moneyFormat) {
  /** @type {Record<string, [number, string, string]>} decimals, thousands separator, decimal separator */
  const formats = {
    amount: [2, ',', '.'],
    amount_no_decimals: [0, ',', '.'],
    amount_with_comma_separator: [2, '.', ','],
    amount_no_decimals_with_comma_separator: [0, '.', ','],
    amount_with_space_separator: [2, ' ', ','],
    amount_no_decimals_with_space_separator: [0, ' ', ','],
    amount_with_period_and_space_separator: [2, ' ', '.'],
    amount_with_apostrophe_separator: [2, "'", '.'],
  };

  return moneyFormat.replace(/{{\s*(\w+)\s*}}/g, (_, token) => {
    const [decimals, thousands, separator] = formats[token] ?? [2, ',', '.'];
    const [integer = '', fraction] = (cents / 100).toFixed(decimals).split('.');
    return integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? separator + fraction : '');
  });
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}

/** Whether the lines the page came with have been seen: one that appears later, already reached, just got there. */
let pageLinesSeen = false;

/**
 * The free-shipping line (snippets/cart-free-shipping.liquid). Celebrates once when the cart reaches free shipping,
 * whether a quantity change here or the server's redraw says so. A cart that was already there when the page
 * loaded gets nothing.
 */
class CartFreeShipping extends HTMLElement {
  static observedAttributes = ['data-reached'];

  /** @type {IntersectionObserver | null} Waits until the line can be seen: the cart drawer may still be closed. */
  #whenSeen = null;

  /**
   * @param {string} _name - The attribute, always data-reached.
   * @param {string | null} was - Its value before.
   * @param {string | null} now - Its value now.
   */
  attributeChangedCallback(_name, was, now) {
    this.#whenSeen?.disconnect();
    if (now !== 'true' || !(was === 'false' || (was === null && pageLinesSeen)) || prefersReducedMotion()) return;

    const whenSeen = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        whenSeen.disconnect();
        this.#celebrate();
      },
      // All of it: the drawer slides in from the side, and the line is in view only once it has arrived.
      { threshold: 1 }
    );
    this.#whenSeen = whenSeen;
    whenSeen.observe(this);
    // Still out of sight after this long (a drawer left closed, a line scrolled away): no longer news.
    setTimeout(() => whenSeen.disconnect(), 5000);
  }

  disconnectedCallback() {
    this.#whenSeen?.disconnect();
  }

  /**
   * The sentence rises in, its tick is drawn, a light runs along the full bar, the truck bounces on the gift and
   * confetti flies from it. Played by script, not by a class: the redraw that follows a quantity change puts the
   * server's classes back and would cut it short.
   */
  #celebrate() {
    const sentence = this.querySelector('.cart-free-shipping__reached');
    const tick = this.querySelector('.cart-free-shipping__check path');
    const bar = this.querySelector('.cart-free-shipping__bar');
    const knob = this.querySelector('.cart-free-shipping__knob');
    const easing = 'cubic-bezier(0.33, 1, 0.68, 1)';

    sentence?.animate(
      [
        { opacity: 0, transform: 'translateY(0.5em) scale(0.9)' },
        { opacity: 1, transform: 'translateY(0) scale(1.08)', offset: 0.6 },
        { opacity: 1, transform: 'translateY(0) scale(1)' },
      ],
      { duration: 550, easing }
    );

    if (tick instanceof SVGGeometryElement) {
      const length = tick.getTotalLength();
      tick.animate(
        [
          { strokeDasharray: length, strokeDashoffset: length },
          { strokeDasharray: length, strokeDashoffset: 0 },
        ],
        { duration: 400, delay: 250, easing, fill: 'backwards' }
      );
    }

    bar?.animate([{ transform: 'translateX(-100%)' }, { transform: 'translateX(100%)' }], {
      duration: 800,
      delay: 200,
      easing: 'ease-in-out',
      pseudoElement: '::after',
    });

    // The truck is still on its way to the gift: the bounce and the confetti wait for it.
    const arrival = 450;
    knob?.animate(
      [
        { transform: 'scale(1) rotate(0)' },
        { transform: 'scale(1.45) rotate(-12deg)', offset: 0.3 },
        { transform: 'scale(0.92) rotate(8deg)', offset: 0.6 },
        { transform: 'scale(1.08) rotate(-3deg)', offset: 0.8 },
        { transform: 'scale(1) rotate(0)' },
      ],
      { duration: 700, delay: arrival, easing }
    );
    if (knob) setTimeout(() => this.isConnected && this.#confetti(knob), arrival);
  }

  /**
   * Throws confetti up from an element. The pieces live in a popover on the body: that is above the cart drawer,
   * and outside everything a redraw of the cart touches.
   * @param {Element} from - Where it starts.
   */
  #confetti(from) {
    const layer = document.createElement('div');
    if (typeof layer.showPopover !== 'function') return;

    const { left, top, width, height } = from.getBoundingClientRect();
    const tokens = ['--color-success', '--color-instock', '--color-lowstock', '--color-foreground'];
    const style = getComputedStyle(this);
    const colours = tokens.map((token) => style.getPropertyValue(token).trim()).filter(Boolean);
    if (!width || !colours.length) return;

    layer.className = 'cart-free-shipping-confetti';
    layer.popover = 'manual';
    layer.setAttribute('aria-hidden', 'true');
    document.body.append(layer);
    layer.showPopover();

    let longest = 0;
    for (let i = 0; i < 44; i++) {
      const piece = document.createElement('i');
      const size = 5 + Math.random() * 5;
      // Mostly upwards, and more to the left: the gift is at the right end of the bar.
      const angle = -Math.PI / 2 + (Math.random() - 0.62) * 2.4;
      const reach = 60 + Math.random() * 130;
      const x = Math.cos(angle) * reach;
      const y = Math.sin(angle) * reach;
      const spin = (Math.random() - 0.5) * 900;
      const duration = 1100 + Math.random() * 700;
      longest = Math.max(longest, duration);

      piece.style.cssText = `left:${left + width / 2}px;top:${top + height / 2}px;width:${size}px;height:${
        i % 3 ? size * 0.5 : size
      }px;border-radius:${i % 3 ? '1px' : '50%'};background:${colours[i % colours.length]}`;
      layer.append(piece);
      piece.animate(
        [
          { transform: 'translate(-50%, -50%) rotate(0)', opacity: 1 },
          { transform: `translate(${x}px, ${y}px) rotate(${spin * 0.6}deg)`, opacity: 1, offset: 0.35, easing: 'ease-in' },
          { transform: `translate(${x * 1.25}px, ${y + 190}px) rotate(${spin}deg)`, opacity: 0 },
        ],
        { duration, easing: 'cubic-bezier(0.1, 0.8, 0.3, 1)', fill: 'forwards' }
      );
    }
    setTimeout(() => layer.remove(), longest + 100);
  }
}

if (!customElements.get('cart-free-shipping')) {
  customElements.define('cart-free-shipping', CartFreeShipping);
  pageLinesSeen = true;
}
